import type { Action, ActionContext } from '../types';

type AnyJson =
  | null
  | boolean
  | number
  | string
  | AnyJson[]
  | { [k: string]: AnyJson };

type StorageGetter = (key: string) => string | null;

/**
 * Resolves values from session/local storage.
 *
 * @param key - Storage key to retrieve.
 * @returns Stored value or `null` when missing/unavailable.
 */
export const defaultCtxGetter: StorageGetter = (key) => {
  try {
    if (typeof sessionStorage !== 'undefined') {
      const v = sessionStorage.getItem(key);
      if (v !== null) return v;
    }
  } catch {}

  try {
    if (typeof localStorage !== 'undefined') {
      const v = localStorage.getItem(key);
      if (v !== null) return v;
    }
  } catch {}

  return null;
};

const defaultStrategies = [
  {
    // Matches any placeholder whose inner value starts with `ctx/`.
    // Using a string pattern enables simple prefix checks in the inflator.
    pattern: 'ctx',
    resolve: (inner: string) => defaultCtxGetter(inner),
  },
];

export function inflateAtPlaceholders<T extends AnyJson>(
  input: T,
  opts?: {
    context?: ActionContext;
    // what to do if ctx value is missing:
    // - "keep": keep the original "@{ctx/...}" text (default)
    // - "empty": replace with ""
    onMissing?: 'keep' | 'empty';
    // extensible placeholder strategies
    // each strategy provides a pattern to match the inner content and a resolver
    strategies?: Array<{
      pattern: RegExp | string;
      resolve: (inner: string) => string | null;
    }>;
  }
): T {
  /**
   * Replaces `@{...}` placeholders within strings, arrays, and objects.
   *
   * @param input - JSON-like structure to inflate.
   * @param opts - Options controlling resolution and fallback behavior.
   * @returns Copy of `input` with placeholders resolved.
   */
  const onMissing = opts?.onMissing ?? 'keep';

  const composedStrategies: Array<{
    pattern: RegExp | string;
    resolve: (inner: string, context?: ActionContext) => string | null;
  }> = [
    ...defaultStrategies,
    // external strategies (if any)
    ...((opts?.strategies ?? []) as Array<{
      pattern: RegExp | string;
      resolve: (inner: string) => string | null;
    }>),
  ];

  /**
   * Builds a replacer for `@{...}` placeholders.
   *
   * @param strategies - Ordered strategies for resolution.
   * @param onMissing - Missing resolution policy.
   * @param context - Optional runtime context for resolvers.
   * @returns Replacer for `String.prototype.replace`.
   */
  const buildPlaceholderReplacer = (
    strategies: Array<{
      pattern: RegExp | string;
      resolve: (inner: string, context?: ActionContext) => string | null;
    }>,
    onMissing: 'keep' | 'empty',
    context?: ActionContext
  ) => {
    return (full: string, inner: string): string => {
      if (typeof inner !== 'string') return full;

      for (const { pattern, resolve } of strategies) {
        const matches =
          typeof pattern === 'string'
            ? inner.startsWith(`${pattern}/`) || inner === pattern
            : pattern.test(inner);
        if (matches) {
          const v = resolve(inner, context);
          if (v !== null) return v;
          return onMissing === 'empty' ? '' : full;
        }
      }

      return full;
    };
  };

  const inflateString = (s: string, context?: ActionContext): string => {
    const re = /@\{([^}]+)\}/g; // captures inside @{...}
    const replacer = buildPlaceholderReplacer(
      composedStrategies,
      onMissing,
      context
    );
    return s.replace(re, replacer);
  };

  /**
   * Walks JSON-like data and inflates placeholders in string values.
   *
   * @param value - JSON-like input to traverse.
   * @param context - Optional runtime context for string inflation.
   * @returns New structure with placeholders inflated.
   */
  const walk = (value: AnyJson, context?: ActionContext): AnyJson => {
    if (typeof value === 'string') {
      return value.includes('@{') ? inflateString(value, context) : value;
    }

    if (Array.isArray(value)) {
      return value.map((v) => walk(v, context)) as AnyJson;
    }

    if (value && typeof value === 'object') {
      const out: Record<string, AnyJson> = {};
      for (const [k, v] of Object.entries(value)) {
        if (k === 'inflateData') {
          out[k] = v; // keep it unchanged, but do not traverse into it
          continue;
        }
        out[k] = walk(v as AnyJson, context);
      }
      return out as AnyJson;
    }

    return value; // number | boolean | null
  };

  return walk(input, opts?.context) as T;
}

export function prepareActionWithContext(
  action: Action,
  ctx: ActionContext
): Action {
  /**
   * Prepares an action by inflating placeholders in `options` when requested.
   *
   * @param action - Action whose `options` may contain placeholders.
   * @param ctx - Runtime context for placeholder resolution.
   * @returns Action with inflated `options` or the original action.
   */
  if ((action?.options as any).inflateData) {
    return {
      ...action,
      options: inflateAtPlaceholders(action.options as any, {
        context: ctx,
      }),
    };
  } else {
    return action;
  }
}
